import { useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDroppable } from '@dnd-kit/core';
import { api } from '@/lib/api';
import { weekMondayFromOffset, snap15, type Block } from '@focus/shared';
import { useUI, visibleHourRange } from '@/stores/ui';
import { Icon } from './icon';
import { cn } from '@/lib/cn';
import { BlockContextMenu, type BlockCat } from './block-context-menu';
import { NowLine } from './now-line';
import { SleepOverlay, useSleepSegments } from './sleep-overlay';

const MIN_HOUR_H = 28;
const GUTTER_W_DESKTOP = 72;
const GUTTER_W_MOBILE = 44;
const WEEKDAY_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function useIsMobile() {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

import { toneFor } from './block-tones';

export function WeekView() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const GUTTER_W = isMobile ? GUTTER_W_MOBILE : GUTTER_W_DESKTOP;
  const { weekOffset, darkMode, zoomLevel, quietHoursEnabled, quietHoursFrom, quietHoursTo } = useUI();
  const { startHour: START_HOUR, endHour: END_HOUR } = visibleHourRange({
    quietHoursEnabled,
    quietHoursFrom,
    quietHoursTo,
  });
  const HOURS = END_HOUR - START_HOUR;
  const weekStart = useMemo(() => weekMondayFromOffset(weekOffset).getTime(), [weekOffset]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dayRefs = useRef<(HTMLDivElement | null)[]>(Array(7).fill(null));
  const [baseHourH, setBaseHourH] = useState(MIN_HOUR_H);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => {
      const avail = el.clientHeight;
      const fit = Math.floor(avail / HOURS);
      setBaseHourH(Math.max(MIN_HOUR_H, fit));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [HOURS]);
  const hourH = Math.max(MIN_HOUR_H, Math.round(baseHourH * zoomLevel));

  useQuery({ queryKey: ['tasks'], queryFn: api.listTasks });
  const { data: blocks = [] } = useQuery({
    queryKey: ['blocks', weekStart],
    queryFn: () => api.listBlocks(weekStart),
  });

  const patchBlock = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Block> }) => api.patchBlock(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocks', weekStart] }),
  });
  const cascadeShift = useMutation({
    mutationFn: (v: { dayIdx: number; fromS: number; deltaHours: number }) =>
      api.cascadeShift(weekStart, v.dayIdx, v.fromS, v.deltaHours),
  });
  const deleteBlock = useMutation({
    mutationFn: (id: string) => api.deleteBlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocks', weekStart] }),
  });

  const blocksByDay = useMemo(() => {
    const m: Block[][] = Array.from({ length: 7 }, () => []);
    for (const b of blocks) if (b.dayIdx >= 0 && b.dayIdx < 7) m[b.dayIdx].push(b);
    return m;
  }, [blocks]);

  const sleepSegments = useSleepSegments(weekStart);

  const todayIdx = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart + i * 86400000);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() === now.getTime()) return i;
    }
    return -1;
  }, [weekStart]);

  const totalH = HOURS * hourH;

  return (
    <div className="flex flex-col h-full bg-paper dark:bg-dark-paper">
      <div
        className="grid border-b border-line dark:border-dark-line"
        style={{ gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)` }}
      >
        <div />
        {(isMobile ? WEEKDAY_SHORT : WEEKDAY_LONG).map((name, i) => {
          const d = new Date(weekStart + i * 86400000);
          return (
            <div
              key={i}
              className="px-1 md:px-3 py-1.5 md:py-2.5 flex flex-col md:flex-row items-center md:items-baseline gap-0 md:gap-2 border-l border-line dark:border-dark-line truncate"
            >
              <span className="text-[12px] md:text-[15px] font-semibold tracking-[-0.3px] font-display text-fg dark:text-dark-fg">
                {d.getDate()}.
              </span>
              <span className="text-[10px] md:text-[12.5px] font-medium tracking-[-0.1px] truncate text-fg dark:text-dark-fg">
                {name}
              </span>
            </div>
          );
        })}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin">
        <div className="grid relative pt-3" style={{ gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)`, minHeight: '100%' }}>
          <div
            className="relative border-r border-line dark:border-dark-line bg-paper dark:bg-dark-paper"
            style={{ height: totalH }}
          >
            {Array.from({ length: Math.floor(HOURS) + 1 }, (_, i) => {
              const hour = Math.floor(START_HOUR) + i;
              if (hour > END_HOUR) return null;
              const top = (hour - START_HOUR) * hourH;
              return (
                <div
                  key={hour}
                  className="absolute left-0 right-0 flex items-center justify-end pr-1 md:pr-2 pointer-events-none"
                  style={{ top, height: 0 }}
                >
                  <span className="text-[10px] md:text-[12px] font-mono text-black dark:text-white tabular-nums leading-none bg-paper dark:bg-dark-paper px-1.5 md:px-3 py-0.5">
                    {String(hour).padStart(2, '0')}:00
                  </span>
                </div>
              );
            })}
          </div>
          {Array.from({ length: 7 }, (_, dayIdx) => (
            <DayColumn
              key={dayIdx}
              dayIdx={dayIdx}
              weekStart={weekStart}
              isToday={dayIdx === todayIdx}
              darkMode={darkMode}
              blocks={blocksByDay[dayIdx]}
              sleepSegments={sleepSegments}
              hourH={hourH}
              startHour={START_HOUR}
              endHour={END_HOUR}
              hours={HOURS}
              dayRefs={dayRefs}
              onPatch={(id, patch) => patchBlock.mutate({ id, patch })}
              onDelete={(id) => deleteBlock.mutate(id)}
              onCascade={(fromS, deltaHours) => cascadeShift.mutate({ dayIdx, fromS, deltaHours })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DayColumn({
  dayIdx,
  weekStart,
  isToday,
  darkMode,
  blocks,
  sleepSegments,
  hourH,
  startHour,
  endHour,
  hours,
  dayRefs,
  onPatch,
  onDelete,
  onCascade,
}: {
  dayIdx: number;
  weekStart: number;
  isToday: boolean;
  darkMode: boolean;
  blocks: Block[];
  sleepSegments: import('./sleep-overlay').SleepSegment[];
  hourH: number;
  startHour: number;
  endHour: number;
  hours: number;
  dayRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onPatch: (id: string, patch: Partial<Block>) => void;
  onDelete: (id: string) => void;
  onCascade: (fromS: number, deltaHours: number) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `day:${dayIdx}`,
    data: { type: 'day', weekStart, dayIdx, hourH, startHour, endHour },
  });

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
      dayRefs.current[dayIdx] = el;
    },
    [setNodeRef, dayRefs, dayIdx],
  );

  return (
    <div
      ref={setRef}
      className={cn(
        'relative border-l border-line dark:border-dark-line',
        isOver && 'bg-accentSoft dark:bg-accent/25',
      )}
      style={
        {
          ['--cal-row-h' as any]: `${hourH}px`,
          height: hours * hourH,
        } as React.CSSProperties
      }
    >
      <div className="absolute inset-0 cal-grid-line pointer-events-none" />
      <SleepOverlay
        segments={sleepSegments}
        dayIdx={dayIdx}
        hourH={hourH}
        startHour={startHour}
        endHour={endHour}
      />
      {blocks.filter((b) => b.e > startHour && b.s < endHour).map((b) => (
        <BlockCard
          key={b.id}
          block={b}
          darkMode={darkMode}
          hourH={hourH}
          startHour={startHour}
          endHour={endHour}
          dayIdx={dayIdx}
          dayRefs={dayRefs}
          onPatch={onPatch}
          onDelete={onDelete}
          onCascade={onCascade}
        />
      ))}
      {isToday && <NowLine startHour={startHour} endHour={endHour} hourH={hourH} />}
    </div>
  );
}

function BlockCard({
  block,
  darkMode,
  hourH,
  startHour,
  endHour,
  dayIdx,
  dayRefs,
  onPatch,
  onDelete,
  onCascade,
}: {
  block: Block;
  darkMode: boolean;
  hourH: number;
  startHour: number;
  endHour: number;
  dayIdx: number;
  dayRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onPatch: (id: string, patch: Partial<Block>) => void;
  onDelete: (id: string) => void;
  onCascade: (fromS: number, deltaHours: number) => void;
}) {
  const [tempS, setTempS] = useState<number | null>(null);
  const [tempE, setTempE] = useState<number | null>(null);
  const [tempDay, setTempDay] = useState<number | null>(null);
  const [interacting, setInteracting] = useState(false);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const openModal = useUI((s) => s.openModal);

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `block:${block.id}`,
    data: { type: 'block', blockId: block.id, weekStart: block.weekStart },
  });

  const effS = tempS ?? block.s;
  const effE = tempE ?? block.e;
  const effDay = tempDay ?? dayIdx;
  const top = (effS - startHour) * hourH;
  const height = Math.max(4, (effE - effS) * hourH);
  const columnShift = effDay - dayIdx;

  const tone = toneFor(block.cat);
  const contents = Array.isArray(block.contents) ? block.contents : [];
  const contentsDone = contents.filter((c) => c.done).length;
  const contentsTotal = contents.length;

  const startResizeBottom = (e: React.PointerEvent) => {
    if (block.anchor) return;
    e.stopPropagation();
    e.preventDefault();
    setInteracting(true);
    const el = dayRefs.current[dayIdx]!;
    const rect = el.getBoundingClientRect();
    let latestE: number | null = null;
    const move = (ev: PointerEvent) => {
      const y = ev.clientY - rect.top;
      const raw = startHour + y / hourH;
      const snapped = snap15(Math.max(block.s + 0.25, Math.min(endHour, raw)));
      latestE = snapped;
      setTempE(snapped);
    };
    const up = () => {
      setInteracting(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (latestE != null && latestE !== block.e) {
        const delta = latestE - block.e;
        onPatch(block.id, { e: latestE });
        if (delta > 0) onCascade(block.e, delta);
      }
      setTempE(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startResizeTop = (e: React.PointerEvent) => {
    if (block.anchor) return;
    e.stopPropagation();
    e.preventDefault();
    setInteracting(true);
    const el = dayRefs.current[dayIdx]!;
    const rect = el.getBoundingClientRect();
    let latestS: number | null = null;
    const move = (ev: PointerEvent) => {
      const y = ev.clientY - rect.top;
      const raw = startHour + y / hourH;
      const snapped = snap15(Math.max(startHour, Math.min(block.e - 0.25, raw)));
      latestS = snapped;
      setTempS(snapped);
    };
    const up = () => {
      setInteracting(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (latestS != null && latestS !== block.s) {
        onPatch(block.id, { s: latestS });
      }
      setTempS(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startMove = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const role = (e.target as HTMLElement).dataset.role;
    if (role === 'resize-top' || role === 'resize-bottom' || role === 'delete') return;
    e.stopPropagation();
    const startPointerY = e.clientY;
    const startPointerX = e.clientX;
    const startS = block.s;
    const dur = block.e - block.s;
    const startDay = dayIdx;
    let moved = false;
    let latestS: number | null = null;
    let latestDay: number | null = null;
    const move = (ev: PointerEvent) => {
      if (block.anchor) return;
      const dy = ev.clientY - startPointerY;
      const dx = ev.clientX - startPointerX;
      if (!moved && Math.abs(dy) < 3 && Math.abs(dx) < 3) return;
      if (!moved) {
        moved = true;
        setInteracting(true);
      }
      const newS = snap15(Math.max(startHour, Math.min(endHour - dur, startS + dy / hourH)));
      latestS = newS;
      setTempS(newS);
      setTempE(newS + dur);

      for (let i = 0; i < 7; i++) {
        const col = dayRefs.current[i];
        if (!col) continue;
        const r = col.getBoundingClientRect();
        if (ev.clientX >= r.left && ev.clientX <= r.right) {
          latestDay = i;
          setTempDay(i);
          break;
        }
      }
    };
    const up = () => {
      setInteracting(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (!moved) {
        openModal('blockDetail', block.id);
      } else {
        const patch: Partial<Block> = {};
        let dirty = false;
        if (latestS != null && latestS !== block.s) {
          patch.s = latestS;
          patch.e = latestS + dur;
          dirty = true;
        }
        if (latestDay != null && latestDay !== startDay) {
          patch.dayIdx = latestDay;
          dirty = true;
        }
        if (dirty) onPatch(block.id, patch);
      }
      setTempS(null);
      setTempE(null);
      setTempDay(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (block.anchor) return;
    setMenuAt({ x: e.clientX, y: e.clientY });
  };

  const bg = darkMode ? tone.bgDark : tone.bg;
  const ink = darkMode ? tone.inkDark : tone.ink;
  const isTiny = height < 14;
  const isShort = height < 28;

  return (
    <>
      <div
        ref={setDropRef}
        className={cn(
          'absolute rounded-md select-none overflow-hidden',
          isTiny ? 'p-0' : isShort ? 'px-1.5 py-0' : 'px-2 py-1',
          interacting ? 'shadow-pop z-20' : 'shadow-none z-10',
          !interacting && 'transition-[top,height,transform,filter] duration-150 ease-out',
          !block.anchor && 'cursor-grab active:cursor-grabbing hover:brightness-[1.08] dark:hover:brightness-[1.18]',
        )}
        style={{
          top,
          height,
          left: `calc((100% + 1px) * ${columnShift} + 4px)`,
          right: `calc((100% + 1px) * ${-columnShift} + 4px)`,
          background: bg,
          border: `1px solid ${tone.line}`,
          borderLeft: `3px solid ${tone.line}`,
          color: ink,
          boxShadow: isOver ? `inset 0 0 0 2px ${tone.line}` : undefined,
        }}
        title={isShort ? `${block.t} · ${fmtHour(effS)}–${fmtHour(effE)}` : undefined}
        onPointerDown={startMove}
        onContextMenu={onContextMenu}
      >
        {!isTiny && (isShort ? (
          <div className="flex items-center gap-1 font-medium leading-none pointer-events-none h-full text-[10.5px]">
            {block.anchor && <Icon name="lock" size={9} className="opacity-60 shrink-0" />}
            {block.source === 'gcal' && !block.anchor && <Icon name="calendar" size={9} className="opacity-70 shrink-0" />}
            {block.meetingUrl && <Icon name="link" size={9} className="opacity-80 shrink-0" />}
            <span className="tracking-[-0.1px] truncate whitespace-nowrap">{block.t}</span>
            {contentsTotal > 0 && (
              <span className="ml-auto font-mono text-[9px] opacity-70 shrink-0">
                {contentsDone}/{contentsTotal}
              </span>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-start gap-1 font-medium leading-snug pointer-events-none text-[11px]">
              {block.anchor && <Icon name="lock" size={9} className="opacity-60 shrink-0 mt-[3px]" />}
              {block.source === 'gcal' && !block.anchor && <Icon name="calendar" size={10} className="opacity-70 shrink-0 mt-[2px]" />}
              {block.meetingUrl && <Icon name="link" size={10} className="opacity-80 shrink-0 mt-[2px]" />}
              <span className="tracking-[-0.1px] break-words min-w-0">{block.t}</span>
              {contentsTotal > 0 && (
                <span className="ml-auto font-mono text-[9.5px] opacity-70 shrink-0 mt-[1px]">
                  {contentsDone}/{contentsTotal}
                </span>
              )}
            </div>
            <div className="text-[9.5px] font-mono opacity-70 pointer-events-none mt-0.5">
              {fmtHour(effS)}–{fmtHour(effE)}
            </div>
            {contentsTotal > 0 && height > 70 && (
              <BlockInlineContents items={contents} maxVisible={Math.max(1, Math.floor((height - 50) / 14))} ink={ink} />
            )}
          </>
        ))}

        {!block.anchor && (
          <>
            <div
              data-role="resize-top"
              onPointerDown={startResizeTop}
              className="absolute left-0 right-0 top-0 h-1.5 cursor-ns-resize z-10"
              style={{ background: 'transparent' }}
            />
            <div
              data-role="resize-bottom"
              onPointerDown={startResizeBottom}
              className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize z-10"
              style={{ background: 'transparent' }}
            />
          </>
        )}
      </div>
      {menuAt && (
        <BlockContextMenu
          x={menuAt.x}
          y={menuAt.y}
          current={block.cat as BlockCat}
          onPick={(cat) => {
            onPatch(block.id, { cat });
            setMenuAt(null);
          }}
          onDelete={() => {
            onDelete(block.id);
            setMenuAt(null);
          }}
          onClose={() => setMenuAt(null)}
        />
      )}
    </>
  );
}

function fmtHour(h: number) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function BlockInlineContents({
  items,
  maxVisible,
  ink,
}: {
  items: Array<{ taskId: string; done: boolean }>;
  maxVisible: number;
  ink: string;
}) {
  const { data: tasks = [] } = useQuery({ queryKey: ['tasks'], queryFn: api.listTasks });
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const visible = items.slice(0, maxVisible);
  const hiddenCount = items.length - visible.length;
  return (
    <div className="mt-1 space-y-[2px] pointer-events-none">
      {visible.map((c) => {
        const t = byId.get(c.taskId);
        const title = t?.title ?? '(gelöschte Aufgabe)';
        return (
          <div key={c.taskId} className="flex items-center gap-1 text-[10px] leading-[1.25]">
            <span
              className="shrink-0 inline-block w-[8px] h-[8px] rounded-[2px]"
              style={{
                border: `1px solid ${ink}`,
                background: c.done ? ink : 'transparent',
                opacity: 0.7,
              }}
            />
            <span
              className={cn('truncate min-w-0', c.done && 'line-through opacity-60')}
              style={{ color: ink }}
            >
              {title}
            </span>
          </div>
        );
      })}
      {hiddenCount > 0 && (
        <div className="text-[9.5px] font-mono opacity-60" style={{ color: ink }}>
          + {hiddenCount} weitere
        </div>
      )}
    </div>
  );
}
